export const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

export function slugify(str) {
	if (!str && typeof str !== 'string') return str;
	return str.toLowerCase().trim().replace(/\s+/g, '-');
}

export function addSlashes(str) {
	if (!str && typeof str !== 'string') return str;

	const prefix = str.startsWith('/') ? '' : '/';
	const suffix = str.endsWith('/') ? '' : '/';
	return prefix + str + suffix;
}

export function mapBanner(bannerData) {
	if (!bannerData) return null;

	const { type, image, text, imageAlt, link } = bannerData;
	const isTextType = type === 'text';

	if (isTextType && !text) return null; // If text type, text is required
	if (!isTextType && !image) return null; // If not text type, image is required

	return {
		banner: {
			contentCarousel: {
				display: {
					desktopSrc: !isTextType ? image : null,
					mobileSrc: !isTextType ? image : null,
					tabletSrc: !isTextType ? image : null,

					label: isTextType ? text : imageAlt,
					path: link,
				}
			}
		}
	};
}

/**
 * Merges global inherited lines with per-movie overrides.
 * Used for both copyright lines and footer links.
 *
 * Supports three cases:
 *   - perMovieData is null/undefined → full inheritance (return global as-is)
 *   - perMovieData is an Array → legacy full-copy (return as-is for backwards compat)
 *   - perMovieData is an override descriptor → merge global + overrides/deleted/additions
 *
 * @param {Array} globalLines - Lines from Nav and Legal
 * @param {Object|Array|null} perMovieData - Per-movie override descriptor or legacy array
 * @param {Object} opts - Options: { lockFirst: true } to lock index 0 (e.g. Powster copyright line)
 */
export function mergeInheritedLines(globalLines, perMovieData, opts = {}) {
	const { lockFirst = false } = opts;

	if (!perMovieData) {
		return (globalLines || []).map((line, i) => ({
			...line,
			_source: 'global',
			_globalIndex: i,
			_overridden: false,
			_hidden: false,
			_locked: lockFirst && i === 0,
		}));
	}

	if (Array.isArray(perMovieData)) {
		const globals = globalLines || [];
		// Treat the array as "reordered" if any inherited item sits at a non-original index.
		// In that state every inherited item carries the override label; if every inherited
		// item is back at its original index, none of them do. Custom additions never count.
		const isReordered = perMovieData.some((line, i) => {
			const globalAtIndex = globals[i];
			const matchesIndex = globalAtIndex && line.name === globalAtIndex.name && line.destination === globalAtIndex.destination;
			if (matchesIndex) return false;
			return globals.some(g => g.name === line.name && g.destination === line.destination);
		});
		return perMovieData.map((line, i) => {
			const existsInGlobal = globals.some(g => g.name === line.name && g.destination === line.destination);
			return {
				...line,
				_source: 'legacy',
				_globalIndex: i,
				_overridden: isReordered && existsInGlobal,
				_hidden: false,
				_locked: lockFirst && i === 0,
			};
		});
	}

	// "deleted" (copyright) and "hidden" (links) are the same concept
	const excluded = new Set([...(perMovieData.deleted || []), ...(perMovieData.hidden || [])]);
	const overrides = perMovieData.overrides || {};
	const additions = perMovieData.additions || [];

	const result = [];
	(globalLines || []).forEach((line, i) => {
		const isHidden = excluded.has(i);
		const override = overrides[String(i)];
		result.push({
			...line,
			...(override || {}),
			_source: 'global',
			_globalIndex: i,
			_overridden: !!override,
			_hidden: isHidden,
			_locked: lockFirst && i === 0,
		});
	});
	additions.forEach((line, i) => {
		result.push({
			...line,
			_source: 'addition',
			_additionIndex: i,
			_overridden: false,
			_hidden: false,
			_locked: false,
		});
	});
	return result;
}

// Backwards-compatible alias
export const mergeCopyrightLines = (globalLines, perMovieData) =>
	mergeInheritedLines(globalLines, perMovieData, { lockFirst: true });

// Gets a standard thundr background (`bg` value in appData, useable by most bolt pages)
//   based on CMS page data ; defaults to hero assets
export function getHeroBackground(cmsData, page = 'home') {
	const bg = cmsData?.pages?.[page]?.bg;
	const hero = cmsData?.hero;
	const type = bg?.type || hero?.type;

	if (type === 'video') {
		return {
			desktop: {
				asset: bg?.desktop?.video?.custom ? bg.desktop.video.url : hero?.desktop?.video?.url,
				preLoadImg: bg?.desktop?.fallbackImg?.custom ? bg.desktop.fallbackImg.url : hero?.desktop?.fallbackImg?.url,
				alt: bg?.desktop?.video?.custom ? bg.desktop.video.alt : hero?.desktop?.video?.alt,
			},
			tablet: null,
			mobile: {
				asset: bg?.mobile?.video?.custom ? bg.mobile.video.url : hero?.mobile?.video?.url,
				preLoadImg: bg?.mobile?.fallbackImg?.custom ? bg.mobile.fallbackImg.url : hero?.mobile?.fallbackImg?.url,
				alt: bg?.mobile?.video?.custom ? bg.mobile.video.alt : hero?.mobile?.video?.alt,
			},
		};
	}

	return {
		desktop: { asset: bg?.desktop?.still?.custom ? bg.desktop.still.url : hero?.desktop?.still?.url },
		tablet: null,
		mobile: { asset: bg?.mobile?.still?.custom ? bg.mobile.still.url : hero?.mobile?.still?.url },
	};
}